import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, MapPin, ArrowRight, Ticket } from "@phosphor-icons/react";

export default function EventsSection({ serviceKey, serviceName }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/events?service=${serviceKey}&upcoming=true`)
      .then((r) => setEvents(r.data))
      .finally(() => setLoading(false));
  }, [serviceKey]);

  if (loading || events.length === 0) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-8" data-testid="events-section">
      <div className="border-t border-black/10 pt-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="label-eyebrow">Upcoming events</p>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-none mt-3">
              Fight nights<br />on the calendar.
            </h2>
          </div>
          <p className="font-mono text-xs text-black/40">{events.length} EVENT{events.length === 1 ? "" : "S"} SCHEDULED</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {events.map((ev) => {
            const d = ev.event_date ? new Date(ev.event_date) : null;
            const day = d ? d.toLocaleDateString("en-GB", { day: "2-digit" }) : "TBA";
            const month = d ? d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase() : "";
            const year = d ? d.getFullYear() : "";
            const time = d ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";

            return (
              <article
                key={ev.id}
                className="border border-black bg-white hover:bg-black hover:text-white transition-colors group flex flex-col"
                data-testid={`event-card-${ev.id}`}
              >
                {ev.poster_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-neutral-100 relative">
                    <img src={ev.poster_url} alt={ev.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="border border-current p-3 text-center min-w-[64px]">
                      <p className="font-display text-2xl font-black leading-none">{day}</p>
                      <p className="font-mono text-[10px] mt-1 opacity-70">{month}</p>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-lg font-black tracking-tight leading-tight">{ev.title}</h3>
                      {time && <p className="font-mono text-xs opacity-60 mt-1">{time} · {year}</p>}
                    </div>
                  </div>
                  {ev.venue && (
                    <p className="flex items-center gap-2 text-sm opacity-80 mb-2">
                      <MapPin size={14} /> {ev.venue}
                    </p>
                  )}
                  {ev.description && <p className="text-sm opacity-70 leading-relaxed mb-4 line-clamp-3">{ev.description}</p>}
                  {ev.ticket_from != null && (
                    <p className="flex items-center gap-2 text-sm font-semibold mb-4">
                      <Ticket size={14} /> Tickets from GHS {Number(ev.ticket_from).toFixed(2)}
                    </p>
                  )}
                  <Link
                    to={`/book/${serviceKey}`}
                    className="mt-auto inline-flex items-center gap-2 font-semibold text-sm border-t border-current pt-4"
                    data-testid={`event-book-${ev.id}`}
                  >
                    Book tickets <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
